/**
 * MapLibre basemap, skinned to the DedSec palette.
 *
 * The basemap is a single static PMTiles file read in-browser via the pmtiles
 * protocol — no tile server. If that file is absent the map still works: it
 * falls back to a bare dark background, and the extruded buildings alone carry
 * the city. That fallback is deliberate, because the PMTiles extract is ~100MB
 * and should not be a hard dependency of a first run.
 */

import maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";

import { withinData } from "../lib/geo.js";
import { onThemeChange, palette } from "../theme/palette.js";
import "maplibre-gl/dist/maplibre-gl.css";

import { DATA_BOUNDS, DENVER } from "../lib/geo.js";

// Served from /opsec/basetiles/, not /opsec/data/basetiles/, even though the
// files live under data/ on disk. The tiles are stored gzipped exactly as the
// archive held them, and only the basetiles location declares
// Content-Encoding: gzip. Reached through the data location the browser hands
// MapLibre the compressed bytes and its worker says so: "Unable to parse the
// tile ... make sure the data is not gzipped".
const BASETILES_DIR = "./basetiles/";

/** Read tokens from CSS so the map and the HUD can never drift apart. */
function token(name, fallback) {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
}

/** Bare style: background only. Used when no PMTiles extract is present. */
/**
 * Glyphs are vendored into the bundle (see scripts/fetch-glyphs.mjs) rather
 * than pulled from a CDN, so labels do not make the map depend on a third
 * party at runtime.
 */
const GLYPHS = "./fonts/{fontstack}/{range}.pbf";
const FONT = ["Noto Sans Regular"];

function emptyStyle() {
  return {
    version: 8,
    glyphs: GLYPHS,
    sources: {},
    layers: [
      { id: "bg", type: "background", paint: { "background-color": token("--black", "#000") } },
    ],
  };
}

/**
 * Protomaps-backed style, desaturated hard.
 *
 * Only the structural layers are drawn — land, water, roads. Labels and POIs
 * are omitted: they fight the data layers and the HUD already carries the
 * terminal-style text.
 */
function pmtilesStyle() {
  const c = palette();
  const black = c.water;
  // The ground reads darker than the token: #111 was bright enough that the
  // city looked washed out and the beacons lost contrast against it. This is
  // the surface everything else is drawn on, so it should sit well back.
  const ground = c.ground;

  return {
    version: 8,
    glyphs: GLYPHS,
    sources: {
      protomaps: {
        type: "vector",
        // Plain {z}/{x}/{y} files, not the pmtiles:// protocol.
        //
        // Reading the archive client-side, MapLibre silently received empty
        // tiles for particular coordinates and whole streets were missing
        // downtown. Not the archive, the data, the bytes, the compression,
        // overzoom, caching, or the library version — each measured and ruled
        // out — so the fix is to stop asking the protocol and let nginx serve
        // the 4,196 tiles it already has on disk.
        // Absolute, but built by joining rather than by URL(): passing the
        // whole template through URL() percent-encodes the braces into
        // %7Bz%7D, and MapLibre then has nothing left to substitute — it
        // requests that literal path for every tile and every one comes back
        // empty. Only the directory goes through URL().
        tiles: [new URL(BASETILES_DIR, document.baseURI).href + "{z}/{x}/{y}.mvt"],
        minzoom: 0,
        maxzoom: 15,
        bounds: [DATA_BOUNDS.west, DATA_BOUNDS.south, DATA_BOUNDS.east, DATA_BOUNDS.north],
        attribution: '<a href="https://protomaps.com">Protomaps</a> © OpenStreetMap',
      },
    },
    layers: [
      // Same grey as `earth`, deliberately. The background is what shows
      // through anywhere the land polygon does not paint, and a black hole in
      // the middle of a city reads as a broken renderer. Panning outside the
      // data is already prevented by maxBounds, so there is nowhere this
      // wrongly implies land.
      { id: "bg", type: "background", paint: { "background-color": ground } },
      {
        id: "earth",
        type: "fill",
        source: "protomaps",
        "source-layer": "earth",
        paint: { "fill-color": ground },
      },
      {
        id: "water",
        type: "fill",
        source: "protomaps",
        "source-layer": "water",
        paint: { "fill-color": black },
      },
      // ── Roads ─────────────────────────────────────────────────────────
      // Protomaps grades roads as highway / major_road / medium_road /
      // minor_road, and the style used to test only for "highway". Everything
      // else fell into one thin grey line, so downtown Broadway drew exactly
      // like the alley behind it and the street grid read as undifferentiated
      // mesh. Each grade now has its own weight and brightness.
      {
        // Service lanes, driveways, alleys and parking aisles: texture, not
        // something to navigate by.
        id: "roads-service",
        type: "line",
        source: "protomaps",
        "source-layer": "roads",
        minzoom: 14,
        filter: ["any",
          ["==", ["get", "kind_detail"], "driveway"],
          ["==", ["get", "kind_detail"], "service"],
          ["==", ["get", "kind_detail"], "parking_aisle"],
          ["==", ["get", "kind_detail"], "alley"],
        ],
        paint: {
          "line-color": c.roadService,
          "line-width": ["interpolate", ["exponential", 1.6], ["zoom"], 14, 0.8, 18, 5],
        },
      },
      {
        id: "roads-minor",
        type: "line",
        source: "protomaps",
        "source-layer": "roads",
        filter: ["==", ["get", "kind"], "minor_road"],
        paint: {
          "line-color": c.roadMinor,
          "line-width": ["interpolate", ["exponential", 1.6], ["zoom"], 11, 0.4, 16, 5, 19, 17],
        },
      },
      {
        id: "roads-medium",
        type: "line",
        source: "protomaps",
        "source-layer": "roads",
        filter: ["==", ["get", "kind"], "medium_road"],
        paint: {
          "line-color": c.roadMedium,
          "line-width": ["interpolate", ["exponential", 1.6], ["zoom"], 10, 0.7, 16, 7, 19, 22],
        },
      },
      {
        id: "roads-major",
        type: "line",
        source: "protomaps",
        "source-layer": "roads",
        filter: ["==", ["get", "kind"], "major_road"],
        paint: {
          "line-color": c.roadMajor,
          "line-width": ["interpolate", ["exponential", 1.6], ["zoom"], 10, 1.1, 16, 9, 19, 28],
        },
      },
      {
        // Motorways. Brightest and heaviest — and drawn last so an
        // interchange reads as passing over the streets beneath it.
        id: "roads-highway",
        type: "line",
        source: "protomaps",
        "source-layer": "roads",
        filter: ["==", ["get", "kind"], "highway"],
        paint: {
          "line-color": c.roadHighway,
          "line-width": ["interpolate", ["exponential", 1.6], ["zoom"], 9, 1.4, 16, 12, 19, 36],
        },
      },
      // ── Street names ──────────────────────────────────────────────────
      // Drawn along the line rather than as points, and only from z14 where
      // there is room. A dark halo keeps them legible over the extrusions,
      // which is the whole reason they can sit above the 3D layer.
      {
        id: "road-labels",
        type: "symbol",
        source: "protomaps",
        "source-layer": "roads",
        minzoom: 13.5,
        filter: ["has", "name"],
        layout: {
          "symbol-placement": "line",
          "text-field": ["get", "name"],
          "text-font": FONT,
          "text-size": ["interpolate", ["linear"], ["zoom"], 14, 9, 18, 13],
          "text-letter-spacing": 0.12,
          "text-max-angle": 35,
          "text-padding": 4,
          "symbol-spacing": 260,
        },
        paint: {
          "text-color": c.roadLabel,
          "text-halo-color": c.roadLabelHalo,
          "text-halo-width": 1.6,
          "text-halo-blur": 0.4,
        },
      },
    ],
  };
}

/**
 * Is there a real PMTiles archive at that path?
 *
 * A HEAD request is not enough. A dev server with SPA fallback answers 200
 * with index.html for any unknown path, so a missing archive looks present,
 * MapLibre then tries to parse HTML as tiles, and the map's `load` event never
 * fires — the whole app hangs on the boot screen with no error.
 *
 * So check the actual bytes: a PMTiles v3 archive begins with the ASCII magic
 * "PMTiles". HTML never does.
 */
/**
 * Is there a real PMTiles archive at that path?
 *
 * Exported because the data layers ask the same question about their own
 * archives, and for the same reason: a dev server with SPA fallback answers
 * 200 with index.html for anything missing, so a HEAD request cannot tell a
 * present archive from an absent one.
 */
export async function archiveExists(url) {
  try {
    const response = await fetch(url, { headers: { Range: "bytes=0-6" } });
    if (!response.ok) return false;
    const magic = new Uint8Array(await response.arrayBuffer());
    return String.fromCharCode(...magic.slice(0, 7)) === "PMTiles";
  } catch {
    return false;
  }
}

async function pmtilesAvailable() {
  try {
    // The tile the whole world sits under. The archive is still on disk and
    // still what these were dumped from, but it is no longer what the map
    // reads, so its presence says nothing about whether the map will draw.
    const response = await fetch(`${BASETILES_DIR}0/0/0.mvt`, { method: "HEAD" });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Move to the viewer's own location, if they have one and it is in Denver.
 *
 * Never awaited by boot. The permission prompt is answered by a human, and a
 * human may take ten seconds or never answer at all — holding the map on a
 * loading screen for that would be a worse experience than simply starting
 * downtown and easing over once the answer arrives.
 *
 * Someone outside Denver is left downtown on purpose. There is no data where
 * they are, so honouring their location would open the map on empty ground
 * and look broken.
 */
export function locateViewer(map, { onResult } = {}) {
  if (!navigator.geolocation) return;

  navigator.geolocation.getCurrentPosition(
    ({ coords }) => {
      const { longitude, latitude } = coords;
      if (!withinData(longitude, latitude)) {
        onResult?.({ located: false, reason: "outside" });
        return;
      }
      map.easeTo({
        center: [longitude, latitude],
        zoom: Math.max(map.getZoom(), 15.5),
        duration: 2000,
        essential: true,
      });
      onResult?.({ located: true, center: [longitude, latitude] });
    },
    // Denied, unavailable or timed out — all the same to the map: stay put.
    (error) => onResult?.({ located: false, reason: error.code === 1 ? "denied" : "unavailable" }),
    { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
  );
}

/**
 * Was the last touch a single-finger tap, or part of a gesture?
 *
 * MapLibre fires `click` at the end of a pan and at the end of a pinch, which
 * is fine for a map that only reads clicks and wrong for one that acts on them:
 * dragging across the city to look for somewhere, or pinching to see it better,
 * both ended with a destination dropped wherever your finger happened to lift.
 *
 * A tap is one finger, that never became two, that moved less than a thumb's
 * width. Everything else is a gesture and is not asking for anything.
 */
export function watchTaps(map) {
  const canvas = map.getCanvas();
  const state = { tap: true };
  if (!canvas) return () => true;

  /** Finger travel that still counts as holding still, in CSS pixels. */
  const SLOP = 14;

  let start = null;
  let maxTouches = 0;

  canvas.addEventListener("touchstart", (event) => {
    maxTouches = Math.max(maxTouches, event.touches.length);
    if (event.touches.length === 1 && maxTouches === 1) {
      const t = event.touches[0];
      start = { x: t.clientX, y: t.clientY };
    } else {
      start = null;
    }
    state.tap = false;
  }, { passive: true });

  canvas.addEventListener("touchmove", (event) => {
    maxTouches = Math.max(maxTouches, event.touches.length);
    if (!start || event.touches.length !== 1) { start = null; return; }
    const t = event.touches[0];
    if (Math.hypot(t.clientX - start.x, t.clientY - start.y) > SLOP) start = null;
  }, { passive: true });

  canvas.addEventListener("touchend", (event) => {
    // Only when the last finger leaves, so a pinch that ends one finger at a
    // time is not mistaken for a tap on the way out.
    if (event.touches.length > 0) return;
    state.tap = Boolean(start) && maxTouches === 1;
    start = null;
    maxTouches = 0;
  }, { passive: true });

  // A mouse has no gesture to be confused with.
  canvas.addEventListener("mousedown", () => { state.tap = true; }, { passive: true });

  return () => state.tap;
}

export async function createMap(container) {
  // Register the protocol before any style referencing pmtiles:// is applied.
  const protocol = new Protocol();
  maplibregl.addProtocol("pmtiles", protocol.tile);

  const hasBasemap = await pmtilesAvailable();

  const map = new maplibregl.Map({
    container,
    style: hasBasemap ? pmtilesStyle() : emptyStyle(),
    center: [DENVER.longitude, DENVER.latitude],
    zoom: 14.2,
    // Pitched hard: the whole point is to see the extrusions as a skyline.
    pitch: 58,
    // Past 60 MapLibre calls this experimental, and it is — but it is also
    // the difference between looking *at* a city and standing in it. At 85 the
    // camera sits low enough among the towers to read as street level, which
    // is what the buildings were surveyed for.
    maxPitch: 85,
    bearing: -20,
    antialias: true,
    // No cross-fade on tiles. The default 300ms fade is what made darker
    // patches flash through while panning and settle once the map stopped —
    // tiles were fading in over the background rather than replacing it.
    fadeDuration: 0,
    attributionControl: hasBasemap,
    // Must not be tighter than the basemap extract, or part of the map that
    // exists cannot be reached. These match `BOUNDS` in notes.py and the bbox
    // in scripts/fetch-basemap.sh — change all three together.
    maxBounds: [[-105.25, 39.50], [-104.70, 40.00]],
  });

  // Zoom only: the HUD carries its own compass directly above this group, and
  // two north indicators that can disagree is worse than one.
  // Touch gestures, deliberately split from the desktop defaults:
  //   one finger    — pan
  //   two fingers   — pinch to zoom, the gesture every phone map has
  //   three fingers — tilt, dragged up and down
  //
  // Tilt used to be the two-finger gesture and pinch-zoom was off entirely,
  // because MapLibre puts pinch and tilt on the same two fingers and you end up
  // doing both at once. Giving up the pinch to keep the tilt was the wrong way
  // round: pinching is what everybody's hands already do on a map, and tilt is
  // the occasional deliberate act. Moving tilt to three fingers means neither
  // has to be sacrificed, because nothing else claims three.
  if (window.matchMedia("(pointer: coarse)").matches) {
    map.touchZoomRotate?.enable();

    // Flat, and it cannot be tilted.
    //
    // A phone screen is too small for a tilted city to be worth what it costs:
    // buildings occlude the streets behind them, everything you have placed
    // ends up hidden behind a tower, and the gesture to fix it competes with
    // the gestures for panning and zooming. Straight down, the map is a map.
    //
    // maxPitch is the enforcement rather than a one-off setPitch, so nothing
    // downstream — routing, the follow camera, a stray easeTo — can tilt it
    // back by accident.
    map.setMaxPitch(0);
    map.setPitch(0);
    map.touchPitch?.disable();
    map.dragRotate?.disable();

    // Double-tap-to-zoom off.
    //
    // This map answers a single tap in several places — a destination while
    // routing, a mark or camera while placing one, an incident to read it — and
    // a double tap is two single taps as far as any of those are concerned. So
    // tapping twice on the same spot did the thing twice and then zoomed, which
    // is a hard gesture to take back. Pinch and the zoom buttons both cover
    // zooming, and neither is ambiguous.
    map.doubleClickZoom?.disable();
  } else {
    map.touchPitch?.enable();
    map.touchZoomRotate?.enableRotation();
  }

  applySky(map);

  map.addControl(new maplibregl.NavigationControl({
    visualizePitch: false, showCompass: false, showZoom: true,
  }), "bottom-right");

  // Never wait forever. A style that fails to resolve would otherwise leave
  // the boot screen up with no explanation.
  await new Promise((resolve) => {
    if (map.loaded()) return resolve();
    const done = () => resolve();
    map.once("load", done);
    map.once("error", done);
    setTimeout(done, 8000);
  });

  // Defensive resize. MapLibre tracks container size itself, but a map created
  // while the tab is backgrounded or the container is still zero-height can end
  // up with a canvas locked to the default 400x300 and never request tiles for
  // the real viewport. Nudging it once on load, and again when the element
  // actually gets a box, costs nothing and closes that case.
  map.resize();
  if (typeof ResizeObserver !== "undefined") {
    const node = typeof container === "string" ? document.getElementById(container) : container;
    if (node) new ResizeObserver(() => map.resize()).observe(node);
  }

  return { map, hasBasemap };
}

/**
 * Re-skin the map when the theme changes.
 *
 * `setStyle` replaces every layer, which would normally snap the camera back
 * to the style's defaults — so the current view is captured first and restored
 * after. deck.gl's overlay survives, because it is a control on the map rather
 * than a layer in the style.
 */
/**
 * Sky and fog, which only become visible once the camera tilts up.
 *
 * At low pitch this is off screen entirely; past about 60 degrees it is most
 * of the frame, and without it the city ends in a hard edge against the page
 * background.
 */
export function applySky(map) {
  const c = palette();
  try {
    map.setSky({
      "sky-color": c.sky,
      "horizon-color": c.horizon,
      "fog-color": c.fog,
      "fog-ground-blend": 0.6,
      "horizon-fog-blend": 0.4,
      "sky-horizon-blend": 0.6,
      "atmosphere-blend": 0.8,
    });
  } catch {
    // Older renderers have no sky; the map is still perfectly usable.
  }
}

export function watchTheme(map) {
  return onThemeChange(() => {
    if (!map?.getStyle) return;
    const view = {
      center: map.getCenter(),
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch(),
    };
    map.setStyle(pmtilesStyle(), { diff: false });
    map.once("styledata", () => {
      map.jumpTo(view);
      // setStyle drops the sky with everything else.
      applySky(map);
    });
  });
}
